import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { forwardRef, useMemo } from 'react';

interface RichTextProps {
  html: string;
  className?: string;
  /** When true, anchor links open in a new tab with rel="noopener noreferrer". */
  openLinksInNewTab?: boolean;
}

function sanitizeHtml(html: string, openLinksInNewTab: boolean): string {
  if (!openLinksInNewTab) {
    return DOMPurify.sanitize(html);
  }

  const openLinksInNewTabHook = (node: Element) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  };

  DOMPurify.addHook('afterSanitizeAttributes', openLinksInNewTabHook);
  try {
    return DOMPurify.sanitize(html);
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', openLinksInNewTabHook);
  }
}

export const RichText = forwardRef<HTMLDivElement, RichTextProps>(
  ({ html, className, openLinksInNewTab = false }, ref) => {
    const clean = useMemo(
      () => sanitizeHtml(html, openLinksInNewTab),
      [html, openLinksInNewTab],
    );

    return (
      <div
        ref={ref}
        className={clsx('prose prose-sm max-w-none', className)}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  },
);

RichText.displayName = 'RichText';
